import React, { ReactElement, useCallback } from 'react';
import { setBusinessRecords } from '../../data-layer/business/actions';
import * as businessService from '../../data-layer/business/service';
import * as mediaService from '../../data-layer/media/service';
import { useDispatch } from '../../data-layer/useDispatch';
import { toBusinessPersistenceModel } from '../../mappers/business/toBusinessPersistenceModel';
import { toBusinessPersistenceUI } from '../../mappers/business/toBusinessPersistenceUI';
import { BusinessImageForm } from '../../ui/business/BusinessImageForm';
import { Dialog, DialogContent, DialogTitle } from '../../ui/feedback/Dialog';
import { Typography } from '../../ui/typography/Typography';
import { useBackButton } from '../../utils/useBackButton';
import type { BusinessModel } from '../../data-layer/business/types';

interface BusinessImagePersistenceProps {
    business: BusinessModel;
    title: ReactElement | string;
    size: Inspire.Size;
    property: string;
    cropShape?: 'round' | 'rect';
    trackSuccessChange: VoidFunction;
    trackError: VoidFunction;
}

export function BusinessImagePersistence(props: BusinessImagePersistenceProps) {
    const {
        business,
        title,
        size,
        property,
        cropShape = 'rect',
        trackSuccessChange,
        trackError,
    } = props;
    const dispatch = useDispatch();
    const onNavigateBack = useBackButton(business?.url ?? '/');
    const onSubmit = useCallback(
        async (imageUrl: string, cropArea: Inspire.Area) => {
            const blob = await mediaService.cropImage(imageUrl, cropArea);
            const file = await mediaService.uploadBlob({
                blob,
                fileType: mediaService.FileType.IMAGE,
                mediaSizes: {
                    width: cropArea.width,
                    height: cropArea.height,
                },
            });
            const businessPersistence = toBusinessPersistenceModel(
                toBusinessPersistenceUI(business),
            );

            try {
                const response = await businessService.updateBusiness(business.id, {
                    ...businessPersistence,
                    [property]: file.url,
                });

                dispatch(setBusinessRecords(...response.records.business));
                trackSuccessChange();
                onNavigateBack();
            } catch (_error) {
                trackError();
                throw new Error('Не удалось обновить профиль');
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [business, dispatch, onNavigateBack],
    );

    if (!business) {
        return null;
    }

    return (
        <Dialog onClose={onNavigateBack}>
            <DialogTitle onClose={onNavigateBack}>
                <Typography variant="title3" weight="medium">
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent p={0}>
                <BusinessImageForm cropShape={cropShape} size={size} onSubmit={onSubmit} />
            </DialogContent>
        </Dialog>
    );
}
